import React, { useState, useEffect, useRef } from 'react'
import { 
  Upload, 
  Image, 
  Music, 
  Video, 
  File, 
  Search, 
  Filter, 
  Grid, 
  List, 
  Trash2, 
  Edit3, 
  Tag,
  Folder,
  Plus,
  X,
  Download,
  Eye,
  Copy
} from 'lucide-react'
import enhancedStorageService from '../services/enhancedStorageService'
import toast from 'react-hot-toast'

const MediaLibrary = ({ onClose, onSelectMedia, selectedMediaId = null }) => {
  const [mediaItems, setMediaItems] = useState([])
  const [filteredItems, setFilteredItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [filters, setFilters] = useState({
    type: 'all',
    category: 'all',
    search: ''
  })
  const [categories, setCategories] = useState(['未分类', '图片', '音频', '视频', '文档'])
  const [tags, setTags] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  // 加载媒资数据
  const loadMediaItems = async () => {
    setLoading(true)
    try {
      const items = await enhancedStorageService.getAllMedia(filters)
      setMediaItems(items)
      setFilteredItems(items)
      
      // 提取所有标签
      const allTags = new Set()
      items.forEach(item => {
        item.tags.forEach(tag => allTags.add(tag))
      })
      setTags(Array.from(allTags))
    } catch (error) {
      console.error('加载媒资失败:', error)
      toast.error('加载媒资失败')
    } finally {
      setLoading(false)
    }
  }

  // 初始化加载
  useEffect(() => {
    loadMediaItems()
  }, [filters])

  // 过滤媒资
  useEffect(() => {
    let filtered = [...mediaItems]

    // 按类型过滤
    if (filters.type !== 'all') {
      filtered = filtered.filter(item => item.type === filters.type)
    }

    // 按分类过滤
    if (filters.category !== 'all') {
      filtered = filtered.filter(item => item.category === filters.category)
    }

    // 按搜索词过滤
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      )
    }

    setFilteredItems(filtered)
  }, [mediaItems, filters])

  // 处理文件上传
  const handleFileUpload = async (files) => {
    setUploading(true)
    const uploadPromises = []

    for (const file of files) {
      const uploadPromise = uploadFile(file)
      uploadPromises.push(uploadPromise)
    }

    try {
      await Promise.all(uploadPromises)
      toast.success(`成功上传 ${files.length} 个文件`)
      loadMediaItems() // 重新加载媒资列表
    } catch (error) {
      console.error('批量上传失败:', error)
      toast.error('上传失败')
    } finally {
      setUploading(false)
      setShowUploadModal(false)
    }
  }

  // 上传单个文件
  const uploadFile = async (file) => {
    try {
      // 检查文件大小（限制为50MB）
      if (file.size > 50 * 1024 * 1024) {
        throw new Error('文件过大，请选择小于50MB的文件')
      }

      // 生成缩略图
      let thumbnail = null
      if (file.type.startsWith('image/')) {
        thumbnail = await generateThumbnail(file)
      }

      // 读取文件内容
      const fileData = await readFileAsDataURL(file)

      // 确定文件类型
      const fileType = getFileType(file.type)

      // 创建媒资数据
      const mediaData = {
        name: file.name,
        type: fileType,
        category: getDefaultCategory(fileType),
        tags: [],
        data: fileData,
        size: file.size,
        mimeType: file.type,
        url: fileData,
        thumbnail: thumbnail
      }

      // 保存到存储服务
      await enhancedStorageService.saveMedia(mediaData)

      console.log(`✅ 文件上传成功: ${file.name}`)
    } catch (error) {
      console.error('文件上传失败:', error)
      throw error
    }
  }

  // 生成缩略图
  const generateThumbnail = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      img.onload = () => {
        const maxSize = 200
        let { width, height } = img

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width
            width = maxSize
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height
            height = maxSize
          }
        }

        canvas.width = width
        canvas.height = height
        ctx.drawImage(img, 0, 0, width, height)

        resolve(canvas.toDataURL('image/jpeg', 0.8))
      }

      img.src = URL.createObjectURL(file)
    })
  }

  // 读取文件为DataURL
  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  // 获取文件类型
  const getFileType = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('audio/')) return 'audio'
    if (mimeType.startsWith('video/')) return 'video'
    return 'document'
  }

  // 获取默认分类
  const getDefaultCategory = (fileType) => {
    const categoryMap = {
      image: '图片',
      audio: '音频',
      video: '视频',
      document: '文档'
    }
    return categoryMap[fileType] || '未分类'
  }

  // 删除媒资
  const handleDeleteMedia = async (mediaId) => {
    if (!confirm('确定要删除这个媒资吗？')) return

    try {
      await enhancedStorageService.deleteMedia(mediaId)
      toast.success('媒资已删除')
      loadMediaItems()
    } catch (error) {
      console.error('删除媒资失败:', error)
      toast.error('删除失败')
    }
  }

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedItems.length === 0) return
    if (!confirm(`确定要删除选中的 ${selectedItems.length} 个媒资吗？`)) return

    try {
      const deletePromises = selectedItems.map(id => enhancedStorageService.deleteMedia(id))
      await Promise.all(deletePromises)
      toast.success(`已删除 ${selectedItems.length} 个媒资`)
      setSelectedItems([])
      loadMediaItems()
    } catch (error) {
      console.error('批量删除失败:', error)
      toast.error('批量删除失败')
    }
  }

  // 复制媒资URL
  const handleCopyUrl = (mediaItem) => {
    navigator.clipboard.writeText(mediaItem.url)
    toast.success('URL已复制到剪贴板')
  }

  // 下载媒资
  const handleDownload = (mediaItem) => {
    const link = document.createElement('a')
    link.href = mediaItem.url
    link.download = mediaItem.name
    link.click()
  }

  // 选择媒资
  const handleSelectMedia = (mediaItem) => {
    if (onSelectMedia) {
      onSelectMedia(mediaItem)
    }
  }

  // 获取文件图标
  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return <Image className="w-8 h-8 text-blue-500" />
      case 'audio': return <Music className="w-8 h-8 text-green-500" />
      case 'video': return <Video className="w-8 h-8 text-purple-500" />
      default: return <File className="w-8 h-8 text-gray-500" />
    }
  }

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // 渲染网格视图
  const renderGridView = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {filteredItems.map((item) => (
        <div
          key={item.id}
          className={`relative group bg-dark-light/50 backdrop-blur-md rounded-lg border border-white/10 overflow-hidden cursor-pointer transition-all duration-200 hover:border-primary/50 ${
            selectedMediaId === item.id ? 'ring-2 ring-primary' : ''
          }`}
          onClick={() => handleSelectMedia(item)}
        >
          {/* 选择框 */}
          <div className="absolute top-2 left-2 z-10">
            <input
              type="checkbox"
              checked={selectedItems.includes(item.id)}
              onChange={(e) => {
                e.stopPropagation()
                if (e.target.checked) {
                  setSelectedItems([...selectedItems, item.id])
                } else {
                  setSelectedItems(selectedItems.filter(id => id !== item.id))
                }
              }}
              className="w-4 h-4 text-primary bg-dark-700 border-gray-600 rounded focus:ring-primary"
            />
          </div>

          {/* 媒体预览 */}
          <div className="aspect-square bg-dark-700 flex items-center justify-center">
            {item.thumbnail ? (
              <img
                src={item.thumbnail}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            ) : (
              getFileIcon(item.type)
            )}
          </div>

          {/* 文件信息 */}
          <div className="p-3">
            <h3 className="text-sm font-medium text-white truncate" title={item.name}>
              {item.name}
            </h3>
            <p className="text-xs text-gray-400 mt-1">
              {formatFileSize(item.size)}
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {item.tags.slice(0, 2).map((tag, index) => (
                <span
                  key={index}
                  className="text-xs bg-primary/20 text-primary px-2 py-1 rounded"
                >
                  {tag}
                </span>
              ))}
              {item.tags.length > 2 && (
                <span className="text-xs text-gray-400">
                  +{item.tags.length - 2}
                </span>
              )}
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCopyUrl(item)
              }}
              className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              title="复制URL"
            >
              <Copy className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDownload(item)
              }}
              className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              title="下载"
            >
              <Download className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteMedia(item.id)
              }}
              className="p-2 bg-red-500/20 rounded-full hover:bg-red-500/30 transition-colors"
              title="删除"
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )

  // 渲染列表视图
  const renderListView = () => (
    <div className="space-y-2">
      {filteredItems.map((item) => (
        <div
          key={item.id}
          className={`flex items-center p-3 bg-dark-light/50 backdrop-blur-md rounded-lg border border-white/10 hover:border-primary/50 transition-all duration-200 cursor-pointer ${
            selectedMediaId === item.id ? 'ring-2 ring-primary' : ''
          }`}
          onClick={() => handleSelectMedia(item)}
        >
          <input
            type="checkbox"
            checked={selectedItems.includes(item.id)}
            onChange={(e) => {
              e.stopPropagation()
              if (e.target.checked) {
                setSelectedItems([...selectedItems, item.id])
              } else {
                setSelectedItems(selectedItems.filter(id => id !== item.id))
              }
            }}
            className="w-4 h-4 text-primary bg-dark-700 border-gray-600 rounded focus:ring-primary mr-3"
          />

          <div className="w-12 h-12 bg-dark-700 rounded flex items-center justify-center mr-3">
            {item.thumbnail ? (
              <img
                src={item.thumbnail}
                alt={item.name}
                className="w-full h-full object-cover rounded"
              />
            ) : (
              getFileIcon(item.type)
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-white truncate">
              {item.name}
            </h3>
            <p className="text-xs text-gray-400">
              {formatFileSize(item.size)} • {item.category}
            </p>
            <div className="flex flex-wrap gap-1 mt-1">
              {item.tags.map((tag, index) => (
                <span
                  key={index}
                  className="text-xs bg-primary/20 text-primary px-2 py-1 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCopyUrl(item)
              }}
              className="p-2 hover:bg-white/10 rounded transition-colors"
              title="复制URL"
            >
              <Copy className="w-4 h-4 text-gray-400" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDownload(item)
              }}
              className="p-2 hover:bg-white/10 rounded transition-colors"
              title="下载"
            >
              <Download className="w-4 h-4 text-gray-400" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteMedia(item.id)
              }}
              className="p-2 hover:bg-red-500/20 rounded transition-colors"
              title="删除"
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-dark rounded-2xl w-full max-w-6xl h-[80vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-2xl font-bold text-white">媒资库</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="p-2 hover:bg-white/10 rounded transition-colors"
              title={viewMode === 'grid' ? '切换到列表视图' : '切换到网格视图'}
            >
              {viewMode === 'grid' ? <List className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 工具栏 */}
        <div className="p-6 border-b border-white/10">
          <div className="flex flex-wrap items-center gap-4">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜索媒资..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-dark-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* 类型过滤 */}
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="px-4 py-2 bg-dark-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">所有类型</option>
              <option value="image">图片</option>
              <option value="audio">音频</option>
              <option value="video">视频</option>
              <option value="document">文档</option>
            </select>

            {/* 分类过滤 */}
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-4 py-2 bg-dark-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">所有分类</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            {/* 上传按钮 */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>上传</span>
            </button>

            {/* 批量操作 */}
            {selectedItems.length > 0 && (
              <button
                onClick={handleBatchDelete}
                className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>删除选中 ({selectedItems.length})</span>
              </button>
            )}
          </div>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Folder className="w-16 h-16 mb-4" />
              <p className="text-lg">暂无媒资</p>
              <p className="text-sm">点击上传按钮添加媒资文件</p>
            </div>
          ) : (
            viewMode === 'grid' ? renderGridView() : renderListView()
          )}
        </div>
      </div>

      {/* 上传模态框 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-60 flex items-center justify-center">
          <div className="bg-dark rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">上传媒资</h3>
            
            <div
              className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">点击选择文件或拖拽文件到此处</p>
              <p className="text-sm text-gray-500">支持图片、音频、视频、文档等格式</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt"
              onChange={(e) => {
                if (e.target.files.length > 0) {
                  handleFileUpload(Array.from(e.target.files))
                }
              }}
              className="hidden"
            />

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                disabled={uploading}
              >
                取消
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                disabled={uploading}
              >
                {uploading ? '上传中...' : '选择文件'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MediaLibrary

